import React, { useState, useEffect, useRef } from 'react';
import { User, ShoppingBag, Lock, Edit2, MapPin, Plus, Trash2, Check, Eye, EyeOff, ShoppingCart, RefreshCw } from 'lucide-react';
import { apiService } from '../api/api';
import maleAvatar from '../assets/images/male-avatar.png';
import femaleAvatar from '../assets/images/female-avatar.jpg';

const BASE_URL = 'https://uatapi.ecommerce.ashtamtechnologies.com/api//api/';

const extractAddressId = (address) => {
  if (!address) return null;
  const raw = address.id ?? address.Id ?? address.ID ?? address.addressId ?? address.AddressId ?? address.userAddressId ?? address.UserAddressId ?? null;
  const parsed = parseInt(raw, 10);
  return isNaN(parsed) || parsed <= 0 ? null : parsed;
};

const COUNTRY_PHONE_RULES = [
  { code: '+1', iso: 'US', name: 'USA / Canada', maxLen: 12, pattern: /^\+1[2-9]\d{2}[2-9]\d{6}$/, digits: '10 digits after +1', example: '+12025551234' },
  { code: '+91', iso: 'IN', name: 'India', maxLen: 13, pattern: /^\+91[6-9]\d{9}$/, digits: '10 digits after +91', example: '+919876543210' },
  { code: '+86', iso: 'CN', name: 'China', maxLen: 14, pattern: /^\+86(13|14|15|16|17|18|19)\d{9}$/, digits: '11 digits after +86', example: '+8613812345678' },
  { code: '+81', iso: 'JP', name: 'Japan', maxLen: 13, pattern: /^\+81[789]0\d{8}$/, digits: '10-11 digits after +81', example: '+819012345678' },
  { code: '+82', iso: 'KR', name: 'South Korea', maxLen: 13, pattern: /^\+821[0-9]\d{7,8}$/, digits: '9-10 digits after +82', example: '+821012345678' },
  { code: '+65', iso: 'SG', name: 'Singapore', maxLen: 11, pattern: /^\+65[689]\d{7}$/, digits: '8 digits after +65', example: '+6591234567' },
  { code: '+60', iso: 'MY', name: 'Malaysia', maxLen: 13, pattern: /^\+601[0-9]\d{7,8}$/, digits: '9-10 digits after +60', example: '+60123456789' },
  { code: '+66', iso: 'TH', name: 'Thailand', maxLen: 12, pattern: /^\+66[689]\d{8}$/, digits: '9 digits after +66', example: '+66812345678' },
  { code: '+63', iso: 'PH', name: 'Philippines', maxLen: 13, pattern: /^\+639\d{9}$/, digits: '10 digits after +63', example: '+639171234567' },
  { code: '+62', iso: 'ID', name: 'Indonesia', maxLen: 15, pattern: /^\+628\d{8,11}$/, digits: '9-12 digits after +62', example: '+62812345678' },
  { code: '+92', iso: 'PK', name: 'Pakistan', maxLen: 13, pattern: /^\+923\d{9}$/, digits: '10 digits after +92', example: '+923001234567' },
  { code: '+880', iso: 'BD', name: 'Bangladesh', maxLen: 14, pattern: /^\+8801[3-9]\d{8}$/, digits: '10 digits after +880', example: '+8801712345678' },
  { code: '+971', iso: 'AE', name: 'UAE', maxLen: 13, pattern: /^\+9715\d{8}$/, digits: '9 digits after +971', example: '+971501234567' },
  { code: '+966', iso: 'SA', name: 'Saudi Arabia', maxLen: 13, pattern: /^\+9665\d{8}$/, digits: '9 digits after +966', example: '+966512345678' },
  { code: '+974', iso: 'QA', name: 'Qatar', maxLen: 12, pattern: /^\+974[3-7]\d{7}$/, digits: '8 digits after +974', example: '+97430123456' },
  { code: '+973', iso: 'BH', name: 'Bahrain', maxLen: 12, pattern: /^\+973[3-9]\d{7}$/, digits: '8 digits after +973', example: '+97333123456' },
  { code: '+968', iso: 'OM', name: 'Oman', maxLen: 12, pattern: /^\+9689\d{7}$/, digits: '8 digits after +968', example: '+96891234567' },
  { code: '+972', iso: 'IL', name: 'Israel', maxLen: 13, pattern: /^\+9725\d{8}$/, digits: '9 digits after +972', example: '+972501234567' },
  { code: '+44', iso: 'GB', name: 'UK', maxLen: 13, pattern: /^\+447\d{9}$/, digits: '10 digits after +44', example: '+447911123456' },
  { code: '+49', iso: 'DE', name: 'Germany', maxLen: 15, pattern: /^\+491[567]\d{8,10}$/, digits: '10-12 digits after +49', example: '+4915123456789' },
  { code: '+33', iso: 'FR', name: 'France', maxLen: 12, pattern: /^\+33[67]\d{8}$/, digits: '9 digits after +33', example: '+33612345678' },
  { code: '+39', iso: 'IT', name: 'Italy', maxLen: 13, pattern: /^\+393\d{9}$/, digits: '10 digits after +39', example: '+393123456789' },
  { code: '+34', iso: 'ES', name: 'Spain', maxLen: 12, pattern: /^\+346\d{8}$/, digits: '9 digits after +34', example: '+34612345678' },
  { code: '+31', iso: 'NL', name: 'Netherlands', maxLen: 12, pattern: /^\+316\d{8}$/, digits: '9 digits after +31', example: '+31612345678' },
  { code: '+41', iso: 'CH', name: 'Switzerland', maxLen: 12, pattern: /^\+417[6-9]\d{7}$/, digits: '9 digits after +41', example: '+41761234567' },
  { code: '+43', iso: 'AT', name: 'Austria', maxLen: 14, pattern: /^\+436\d{8,10}$/, digits: '9-11 digits after +43', example: '+436641234567' },
  { code: '+32', iso: 'BE', name: 'Belgium', maxLen: 12, pattern: /^\+324\d{8}$/, digits: '9 digits after +32', example: '+32412345678' },
  { code: '+47', iso: 'NO', name: 'Norway', maxLen: 11, pattern: /^\+474\d{7}$/, digits: '8 digits after +47', example: '+4741234567' },
  { code: '+46', iso: 'SE', name: 'Sweden', maxLen: 12, pattern: /^\+467\d{8}$/, digits: '9 digits after +46', example: '+46701234567' },
  { code: '+45', iso: 'DK', name: 'Denmark', maxLen: 11, pattern: /^\+454\d{7}$/, digits: '8 digits after +45', example: '+4541234567' },
  { code: '+358', iso: 'FI', name: 'Finland', maxLen: 14, pattern: /^\+3584\d{7,8}$/, digits: '8-9 digits after +358', example: '+358412345678' },
  { code: '+48', iso: 'PL', name: 'Poland', maxLen: 12, pattern: /^\+48[4-9]\d{8}$/, digits: '9 digits after +48', example: '+48512345678' },
  { code: '+7', iso: 'RU', name: 'Russia', maxLen: 12, pattern: /^\+7[789]\d{9}$/, digits: '10 digits after +7', example: '+79161234567' },
  { code: '+380', iso: 'UA', name: 'Ukraine', maxLen: 13, pattern: /^\+380[3-9]\d{8}$/, digits: '9 digits after +380', example: '+380501234567' },
  { code: '+30', iso: 'GR', name: 'Greece', maxLen: 13, pattern: /^\+306\d{9}$/, digits: '10 digits after +30', example: '+306901234567' },
  { code: '+351', iso: 'PT', name: 'Portugal', maxLen: 13, pattern: /^\+3519\d{8}$/, digits: '9 digits after +351', example: '+351912345678' },
  { code: '+36', iso: 'HU', name: 'Hungary', maxLen: 13, pattern: /^\+36[237]\d{7,8}$/, digits: '8-9 digits after +36', example: '+36201234567' },
  { code: '+420', iso: 'CZ', name: 'Czech Republic', maxLen: 13, pattern: /^\+420[6-7]\d{8}$/, digits: '9 digits after +420', example: '+420601234567' },
  { code: '+40', iso: 'RO', name: 'Romania', maxLen: 12, pattern: /^\+407\d{8}$/, digits: '9 digits after +40', example: '+40712345678' },
  { code: '+55', iso: 'BR', name: 'Brazil', maxLen: 14, pattern: /^\+55[1-9][1-9]\d{8,9}$/, digits: '10-11 digits after +55', example: '+5511912345678' },
  { code: '+52', iso: 'MX', name: 'Mexico', maxLen: 14, pattern: /^\+521\d{10}$/, digits: '10 digits after +52', example: '+5215512345678' },
  { code: '+54', iso: 'AR', name: 'Argentina', maxLen: 14, pattern: /^\+549\d{10}$/, digits: '10 digits after +54', example: '+5491112345678' },
  { code: '+56', iso: 'CL', name: 'Chile', maxLen: 12, pattern: /^\+569\d{8}$/, digits: '9 digits after +56', example: '+56912345678' },
  { code: '+57', iso: 'CO', name: 'Colombia', maxLen: 13, pattern: /^\+573\d{9}$/, digits: '10 digits after +57', example: '+573001234567' },
  { code: '+27', iso: 'ZA', name: 'South Africa', maxLen: 12, pattern: /^\+276\d{8}$/, digits: '9 digits after +27', example: '+27612345678' },
  { code: '+234', iso: 'NG', name: 'Nigeria', maxLen: 14, pattern: /^\+2347\d{9}$/, digits: '10 digits after +234', example: '+2347012345678' },
  { code: '+20', iso: 'EG', name: 'Egypt', maxLen: 13, pattern: /^\+201[0125]\d{8}$/, digits: '10 digits after +20', example: '+201012345678' },
  { code: '+254', iso: 'KE', name: 'Kenya', maxLen: 13, pattern: /^\+2547\d{8}$/, digits: '9 digits after +254', example: '+254712345678' },
  { code: '+61', iso: 'AU', name: 'Australia', maxLen: 12, pattern: /^\+614\d{8}$/, digits: '9 digits after +61', example: '+61412345678' },
  { code: '+64', iso: 'NZ', name: 'New Zealand', maxLen: 13, pattern: /^\+642\d{7,9}$/, digits: '8-10 digits after +64', example: '+6421234567' },
];

const detectCountryRule = (phone) => {
  if (!phone || !phone.startsWith('+')) return null;
  return [...COUNTRY_PHONE_RULES].sort((a, b) => b.code.length - a.code.length).find(r => phone.startsWith(r.code)) || null;
};

const validatePhoneNumber = (phone) => {
  if (!phone || !phone.trim()) return { valid: false, message: 'Phone number is required' };
  const cleaned = phone.replace(/[\s\-().]/g, '');
  if (!cleaned.startsWith('+')) return { valid: false, message: 'Phone must start with a country code (e.g. +1 for USA, +91 for India)' };
  const rule = detectCountryRule(cleaned);
  if (!rule) {
    if (!/^\+\d{1,4}\d{6,12}$/.test(cleaned)) return { valid: false, message: 'Invalid phone number. Use format: +[country code][number]' };
    return { valid: true, message: '', country: null };
  }
  if (!rule.pattern.test(cleaned)) return { valid: false, message: `Invalid ${rule.name} number. Format: ${rule.digits} (e.g. ${rule.example})`, country: rule };
  return { valid: true, message: '', country: rule };
};

// ─── Skeleton Components ──────────────────────────────────────────────────────
const Sk = ({ w = '100%', h = 16, r = 6, mb = 0 }) => (
  <div style={{ width: w, height: h, borderRadius: r, background: 'linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite', marginBottom: mb }} />
);

const ProfileSkeleton = () => (
  <div>
    <Sk w={200} h={28} mb={8} />
    <Sk w={150} h={14} mb={32} />
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 32 }}>
      <div style={{ width: 100, height: 100, borderRadius: '50%', background: 'linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite', marginBottom: 16 }} />
      <Sk w={140} h={20} mb={8} r={4} />
      <Sk w={180} h={14} r={4} />
    </div>
    <Sk w={120} h={38} r={6} mb={24} />
    {[1, 2, 3, 4].map(i => (
      <div key={i} style={{ marginBottom: 20 }}>
        <Sk w={80} h={13} mb={8} />
        <Sk w='100%' h={42} r={6} />
      </div>
    ))}
  </div>
);

const OrdersSkeleton = () => (
  <div>
    <Sk w={160} h={28} mb={8} />
    <Sk w={200} h={14} mb={24} />
    <Sk w={180} h={38} r={6} mb={24} />
    {[1, 2, 3].map(i => (
      <div key={i} style={{ marginBottom: 20, borderRadius: 10, border: '1px solid #e0e0e0', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', backgroundColor: '#f8f9fa', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div><Sk w={100} h={18} mb={8} /><Sk w={80} h={13} /></div>
          <Sk w={80} h={26} r={12} />
        </div>
        <div style={{ padding: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 16 }}>
            {[1, 2, 3, 4].map(j => <div key={j}><Sk w={60} h={12} mb={6} /><Sk w={80} h={20} /></div>)}
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <Sk w='48%' h={42} r={6} />
            <Sk w='48%' h={42} r={6} />
          </div>
        </div>
      </div>
    ))}
  </div>
);

const AddressesSkeleton = () => (
  <div>
    <Sk w={160} h={28} mb={8} />
    <Sk w={220} h={14} mb={24} />
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, padding: '16px 20px', backgroundColor: '#f8f9fa', borderRadius: 10 }}>
      <div><Sk w={120} h={16} mb={8} /><Sk w={100} h={13} /></div>
      <Sk w={140} h={40} r={8} />
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(340px,1fr))', gap: 20 }}>
      {[1, 2].map(i => (
        <div key={i} style={{ padding: 24, borderRadius: 12, border: '1px solid #e9ecef' }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <Sk w={70} h={24} r={12} /><Sk w={70} h={24} r={12} />
          </div>
          <Sk w={140} h={18} mb={12} />
          <Sk w='100%' h={13} mb={8} />
          <Sk w='80%' h={13} mb={8} />
          <Sk w='60%' h={13} mb={16} />
          <div style={{ display: 'flex', gap: 8, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
            <Sk w='48%' h={38} r={8} /><Sk w='48%' h={38} r={8} />
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ─── Smart Phone Input ────────────────────────────────────────────────────────
const SmartPhoneInput = ({ value = '', onChange, onBlur, error, disabled = false, name = 'phoneNumber', placeholder, inputStyle = {} }) => {
  const cleaned = value.replace(/[\s\-().]/g, '');
  const detected = detectCountryRule(cleaned);
  const digitsTyped = cleaned.replace(/^\+/, '').replace(/\D/g, '').length;
  const maxDigits = detected ? detected.maxLen - 1 : null;
  const isOverMax = maxDigits !== null && digitsTyped > maxDigits;
  const isValidNow = detected && validatePhoneNumber(value).valid;
  const borderColor = error ? '#ea4335' : isOverMax ? '#ea4335' : isValidNow ? '#16a34a' : '#e0e0e0';
  const ALLOWED = /^[+\d\s\-().]*$/;
  const emit = (v) => { if (typeof onChange === 'function') onChange({ target: { name, value: v } }); };
  const handleKeyDown = (e) => {
    if (['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(e.key) || e.ctrlKey || e.metaKey) return;
    if (e.key === '+') { if (e.target.selectionStart !== 0 || value.includes('+')) { e.preventDefault(); return; } }
    if (!/[\d\s\-().]/.test(e.key) && e.key !== '+') { e.preventDefault(); return; }
    if (/\d/.test(e.key) && maxDigits !== null) {
      const sel = value.slice(e.target.selectionStart, e.target.selectionEnd).replace(/\D/g, '').length;
      if (digitsTyped + (1 - sel) > maxDigits) { e.preventDefault(); return; }
    }
  };
  const handlePaste = (e) => {
    e.preventDefault();
    let p = e.clipboardData.getData('text').replace(/[^+\d\s\-().]/g, '');
    const hp = p.includes('+'); p = p.replace(/\+/g, ''); if (hp) p = '+' + p;
    const { selectionStart: s, selectionEnd: en } = e.target;
    let nv = value.slice(0, s) + p + value.slice(en);
    const nc = nv.replace(/[\s\-().]/g, ''); const r = detectCountryRule(nc);
    if (r) nv = nc.slice(0, r.maxLen);
    emit(nv);
  };
  const handleChange = (e) => {
    let v = e.target.value;
    if (!ALLOWED.test(v)) v = v.replace(/[^+\d\s\-().]/g, '');
    if (v.indexOf('+') > 0 || (v.split('+').length - 1) > 1) v = '+' + v.replace(/\+/g, '');
    const vc = v.replace(/[\s\-().]/g, ''); const r = detectCountryRule(vc);
    if (r && vc.length > r.maxLen) v = vc.slice(0, r.maxLen);
    emit(v);
  };
  const counterText = maxDigits !== null ? `${digitsTyped} / ${maxDigits}` : null;
  const counterColor = isOverMax ? '#ea4335' : '#9ca3af';
  return (
    <div>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        {detected && (
          <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, display: 'flex', alignItems: 'center', gap: 4, padding: '0 10px', background: '#f1f5f9', borderTopLeftRadius: '8px', borderBottomLeftRadius: '8px', borderRight: `1.5px solid ${borderColor}`, pointerEvents: 'none', zIndex: 2, whiteSpace: 'nowrap', minWidth: 58 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>{detected.iso}</span>
          </div>
        )}
        <input type="tel" name={name} value={value} onChange={handleChange} onKeyDown={handleKeyDown} onPaste={handlePaste} onBlur={onBlur} disabled={disabled}
          placeholder={placeholder || '+1 202 555 1234  ·  +91 98765 43210  ·  +44 7911 123456'}
          style={{ width: '100%', padding: `11px ${counterText ? '60px' : '14px'} 11px ${detected ? '70px' : '14px'}`, borderRadius: '8px', border: `1.5px solid ${borderColor}`, fontSize: '14px', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s', backgroundColor: disabled ? '#f8f9fa' : '#fff', color: disabled ? '#666' : '#1e293b', cursor: disabled ? 'not-allowed' : 'text', fontFamily: 'inherit', ...inputStyle }}
          onFocus={e => { if (!disabled) e.target.style.borderColor = error ? '#ea4335' : '#018d71'; }}
        />
        {counterText && !disabled && <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 11, fontWeight: 700, color: counterColor, pointerEvents: 'none' }}>{counterText}</span>}
      </div>
      {!disabled && (() => {
        if (detected && isValidNow && !error) return <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 5, fontSize: 11, color: '#16a34a', fontWeight: 600 }}><Check size={11} /> {detected.name} · Valid number</div>;
        if (detected && !isValidNow && !error && value.length > detected.code.length) return <div style={{ marginTop: 5, fontSize: 11, color: '#6b7280' }}>{detected.name} · {detected.digits} · e.g. {detected.example}</div>;
        if (!value) return <div style={{ marginTop: 5, fontSize: 11, color: '#9ca3af' }}>Start with country code · +1 (USA) · +91 (India) · +44 (UK)</div>;
        return null;
      })()}
    </div>
  );
};

const PhoneInput = ({ value, onChange, onBlur, error, disabled }) => (
  <SmartPhoneInput value={value || ''} onChange={onChange} onBlur={onBlur} error={error} disabled={disabled} name="phoneNumber"
    inputStyle={{ borderRadius: '6px', padding: `10px ${value && detectCountryRule(value.replace(/[\s\-().]/g, '')) ? '60px' : '14px'} 10px ${value && detectCountryRule(value.replace(/[\s\-().]/g, '')) ? '70px' : '14px'}` }} />
);

const AddressTypeBadge = ({ isDefault, isBilling, isShipping }) => {
  const badges = [];
  if (isDefault) badges.push({ bg: '#018d71', color: '#fff', label: 'Default', icon: '🏠' });
  if (isShipping) badges.push({ bg: '#34a853', color: '#fff', label: 'Shipping', icon: '📦' });
  if (isBilling) badges.push({ bg: '#ea4335', color: '#fff', label: 'Billing', icon: '💳' });
  return (
    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
      {badges.map((b, i) => (
        <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', backgroundColor: b.bg, color: b.color, fontSize: '11px', fontWeight: 600, borderRadius: '12px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
          <span style={{ fontSize: '12px' }}>{b.icon}</span>{b.label}
        </span>
      ))}
    </div>
  );
};

const AddressPhoneInput = ({ value, onChange, onBlur, error, placeholder }) => (
  <SmartPhoneInput value={value || ''} onChange={e => onChange(e.target.value)} onBlur={onBlur} error={error} placeholder={placeholder} />
);

const DeleteConfirmModal = ({ address, onConfirm, onCancel, deleting }) => (
  <div onClick={e => { if (e.target === e.currentTarget && !deleting) onCancel(); }}
    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.48)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999 }}>
    <div style={{ background: '#fff', borderRadius: 16, padding: '32px 28px', maxWidth: 400, width: '90%', boxShadow: '0 16px 48px rgba(0,0,0,0.22)' }}>
      <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#fff2f2', border: '2px solid #fca5a5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
        <Trash2 size={24} color="#ea4335" />
      </div>
      <div style={{ fontSize: 18, fontWeight: 700, color: '#1a1a1a', textAlign: 'center', marginBottom: 10 }}>Remove Address?</div>
      <div style={{ fontSize: 14, color: '#555', textAlign: 'center', lineHeight: 1.6, marginBottom: 28 }}>
        Are you sure you want to remove the address for <strong style={{ color: '#333' }}>{address?.fullName || 'this contact'}</strong>?<br />This action cannot be undone.
      </div>
      <div style={{ display: 'flex', gap: 12 }}>
        <button onClick={onCancel} disabled={deleting} style={{ flex: 1, padding: '12px 0', fontSize: 14, fontWeight: 600, borderRadius: 8, background: '#fff', color: '#555', border: '1.5px solid #ddd', cursor: deleting ? 'not-allowed' : 'pointer' }}>Cancel</button>
        <button onClick={onConfirm} disabled={deleting} style={{ flex: 1, padding: '12px 0', fontSize: 14, fontWeight: 700, borderRadius: 8, background: deleting ? '#f5a5a5' : '#ea4335', color: '#fff', border: 'none', cursor: deleting ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          {deleting ? <><span style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid rgba(255,255,255,.4)', borderTopColor: '#fff', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />Removing…</> : <><Trash2 size={15} />Yes, Remove</>}
        </button>
      </div>
    </div>
  </div>
);

const AddressForm = ({ initial, onSave, onCancel }) => {
  const d = initial || {};
  const [form, setForm] = useState({
    fullName: d.fullName || '', mobileNo: d.mobileNo || '', alternateMobileNo: d.alternateMobileNo || '',
    email: d.email || '', addressLine1: d.addressLine1 || '', addressLine2: d.addressLine2 || '',
    landmark: d.landmark || '', city: d.city || '', state: d.state || '', country: d.country || '',
    pincode: d.pincode || '', isDefault: d.isDefault || false, isBilling: d.isBilling || false, isShipping: d.isShipping || false,
  });
  const [errors, setErrors] = useState({});
  const set = (f, v) => { setForm(p => ({ ...p, [f]: v })); if (errors[f]) setErrors(p => ({ ...p, [f]: '' })); };
  const toggle = (k) => { setForm(p => ({ ...p, [k]: !p[k] })); setErrors(p => ({ ...p, addressType: '' })); };
  const validate = () => {
    const e = {};
    if (!form.isDefault && !form.isBilling && !form.isShipping) e.addressType = 'Please select at least one address type';
    if (!form.fullName.trim()) e.fullName = 'Full name is required';
    else if (form.fullName.trim().length < 2) e.fullName = 'Full name must be at least 2 characters';
    else if (!/^[a-zA-Z\s'-]+$/.test(form.fullName.trim())) e.fullName = 'Full name can only contain letters, spaces, hyphens, and apostrophes';
    if (!form.mobileNo.trim()) e.mobileNo = 'Mobile number is required';
    else { const r = validatePhoneNumber(form.mobileNo.trim()); if (!r.valid) e.mobileNo = r.message; }
    if (form.alternateMobileNo.trim()) { const r = validatePhoneNumber(form.alternateMobileNo.trim()); if (!r.valid) e.alternateMobileNo = r.message; }
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = 'Please enter a valid email address';
    if (!form.addressLine1.trim()) e.addressLine1 = 'Address Line 1 is required';
    if (!form.city.trim()) e.city = 'City is required';
    if (!form.state.trim()) e.state = 'State / Province is required';
    if (!form.country.trim()) e.country = 'Country is required';
    if (!form.pincode.trim()) e.pincode = 'Pincode / ZIP code is required';
    else if (!/^\d{3,10}$/.test(form.pincode.trim())) e.pincode = 'Pincode must be 3–10 digits';
    return e;
  };
  const handleSubmit = () => { const e = validate(); if (Object.keys(e).length > 0) { setErrors(e); return; } onSave(form); };
  const inp = (err) => ({ width: '100%', padding: '12px 14px', borderRadius: '8px', border: `1.5px solid ${err ? '#ea4335' : '#e0e0e0'}`, fontSize: '14px', outline: 'none', boxSizing: 'border-box', transition: 'all 0.2s', backgroundColor: '#fff' });
  const Lbl = ({ t, r }) => <label style={{ fontSize: '13px', fontWeight: 600, color: '#333', marginBottom: '8px', display: 'block' }}>{t}{r && <span style={{ color: '#ea4335', marginLeft: 3 }}>*</span>}</label>;
  const Err = ({ m }) => m ? <div style={{ fontSize: '12px', color: '#ea4335', marginTop: '6px', fontWeight: 500 }}>⚠️ {m}</div> : null;
  return (
    <div>
      <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: `1px solid ${errors.addressType ? '#fca5a5' : '#e9ecef'}` }}>
        <Lbl t="Address Type(s)" r />
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {[['isDefault', '🏠 Default'], ['isShipping', '📦 Shipping'], ['isBilling', '💳 Billing']].map(([k, l]) => {
            const a = form[k];
            return (
              <label key={k} onClick={() => toggle(k)} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '10px 16px', borderRadius: '8px', border: `2px solid ${a ? '#018d71' : '#ddd'}`, backgroundColor: a ? '#f0faf6' : '#fff', transition: 'all 0.2s', userSelect: 'none' }}>
                <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${a ? '#018d71' : '#d1d5db'}`, background: a ? '#018d71' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {a && <Check size={11} color="white" strokeWidth={3} />}
                </div>
                <span style={{ fontSize: '14px', fontWeight: a ? 600 : 500, color: a ? '#018d71' : '#555' }}>{l}</span>
              </label>
            );
          })}
        </div>
        <Err m={errors.addressType} />
      </div>
      {[
        { f: 'fullName', l: 'Full Name', r: true, t: 'text', ph: 'John Doe' },
        { f: 'email', l: 'Email', r: false, t: 'email', ph: 'john@example.com' },
        { f: 'addressLine1', l: 'Address Line 1', r: true, t: 'text', ph: 'Street address, P.O. box' },
        { f: 'addressLine2', l: 'Address Line 2', r: false, t: 'text', ph: 'Apartment, suite, unit (optional)' },
        { f: 'landmark', l: 'Landmark', r: false, t: 'text', ph: 'Near..., Opposite...' },
      ].map(({ f, l, r, t, ph }) => (
        <div key={f} style={{ marginBottom: '16px' }}>
          <Lbl t={l} r={r} />
          <input type={t} value={form[f]} onChange={e => set(f, e.target.value)} placeholder={ph} style={inp(errors[f])}
            onFocus={e => e.target.style.borderColor = '#018d71'} onBlur={e => e.target.style.borderColor = errors[f] ? '#ea4335' : '#e0e0e0'} />
          <Err m={errors[f]} />
        </div>
      ))}
      <div style={{ marginBottom: '16px' }}>
        <Lbl t="Mobile Number" r />
        <AddressPhoneInput value={form.mobileNo} onChange={v => set('mobileNo', v)}
          onBlur={() => { if (form.mobileNo.trim()) { const r = validatePhoneNumber(form.mobileNo.trim()); if (!r.valid) setErrors(p => ({ ...p, mobileNo: r.message })); } }}
          error={errors.mobileNo} />
        <Err m={errors.mobileNo} />
      </div>
      <div style={{ marginBottom: '16px' }}>
        <Lbl t="Alternate Mobile" />
        <AddressPhoneInput value={form.alternateMobileNo} onChange={v => set('alternateMobileNo', v)}
          onBlur={() => { if (form.alternateMobileNo.trim()) { const r = validatePhoneNumber(form.alternateMobileNo.trim()); if (!r.valid) setErrors(p => ({ ...p, alternateMobileNo: r.message })); else setErrors(p => ({ ...p, alternateMobileNo: '' })); } }}
          error={errors.alternateMobileNo} placeholder="+1 234 567 8900  (optional)" />
        <Err m={errors.alternateMobileNo} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
        {[['city', 'City', 'New York'], ['state', 'State / Province', 'NY']].map(([f, l, ph]) => (
          <div key={f}>
            <Lbl t={l} r />
            <input type="text" value={form[f]} onChange={e => set(f, e.target.value)} placeholder={ph} style={inp(errors[f])}
              onFocus={e => e.target.style.borderColor = '#018d71'} onBlur={e => e.target.style.borderColor = errors[f] ? '#ea4335' : '#e0e0e0'} />
            <Err m={errors[f]} />
          </div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
        <div>
          <Lbl t="Pincode / ZIP" r />
          <input type="text" value={form.pincode} onChange={e => set('pincode', e.target.value.replace(/\D/g, ''))} placeholder="10001" style={inp(errors.pincode)}
            onFocus={e => e.target.style.borderColor = '#018d71'} onBlur={e => e.target.style.borderColor = errors.pincode ? '#ea4335' : '#e0e0e0'} maxLength={10} inputMode="numeric" />
          <Err m={errors.pincode} />
        </div>
        <div>
          <Lbl t="Country" r />
          <input type="text" value={form.country} onChange={e => set('country', e.target.value)} placeholder="United States" style={inp(errors.country)}
            onFocus={e => e.target.style.borderColor = '#018d71'} onBlur={e => e.target.style.borderColor = errors.country ? '#ea4335' : '#e0e0e0'} />
          <Err m={errors.country} />
        </div>
      </div>
      {Object.keys(errors).some(k => errors[k]) && (
        <div style={{ marginBottom: '20px', padding: '14px 18px', backgroundColor: '#fff8f8', border: '1.5px solid #fca5a5', borderRadius: '10px' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: '#dc2626', marginBottom: '8px' }}>🚫 Please fix the following before saving:</div>
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            {Object.entries(errors).filter(([, v]) => v).map(([k, m]) => <li key={k} style={{ fontSize: '13px', color: '#b91c1c', marginBottom: '4px' }}>{m}</li>)}
          </ul>
        </div>
      )}
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', paddingTop: '16px', borderTop: '1px solid #e9ecef' }}>
        <button onClick={onCancel} className="btn" style={{ padding: '12px 24px', fontSize: '14px', fontWeight: 600, borderRadius: '8px', backgroundColor: '#fff', color: '#333', border: '1.5px solid #ddd' }}>Cancel</button>
        <button onClick={handleSubmit} className="btn btn-primary" style={{ padding: '12px 32px', fontSize: '14px', fontWeight: 600, borderRadius: '8px', background: 'linear-gradient(135deg, #018d71 0%, #1557b0 100%)', border: 'none' }}>
          <Check size={16} style={{ marginRight: '6px', verticalAlign: 'text-bottom' }} />Save Address
        </button>
      </div>
    </div>
  );
};

const AddressCard = ({ address, onEdit, onDelete }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div style={{ padding: '24px', backgroundColor: '#fff', borderRadius: '12px', border: `2px solid ${hovered ? '#018d71' : '#e9ecef'}`, boxShadow: hovered ? '0 8px 24px rgba(26,115,232,0.15)' : '0 2px 8px rgba(0,0,0,0.06)', transition: 'all 0.3s ease', height: '100%', display: 'flex', flexDirection: 'column' }}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      <div style={{ marginBottom: '16px' }}><AddressTypeBadge isDefault={address.isDefault} isBilling={address.isBilling} isShipping={address.isShipping} /></div>
      <div style={{ fontSize: '16px', fontWeight: 700, color: '#1a1a1a', marginBottom: '12px' }}>{address.fullName}</div>
      <div style={{ fontSize: '13px', color: '#555', marginBottom: '16px', flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}><span>📞</span><span>{address.mobileNo}</span>{address.alternateMobileNo && <span style={{ color: '#999' }}> / {address.alternateMobileNo}</span>}</div>
        {address.email && <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}><span>✉️</span><span>{address.email}</span></div>}
        <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #f0f0f0', lineHeight: '1.6' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '4px' }}><span style={{ marginTop: '2px' }}>📍</span><span>{address.addressLine1}</span></div>
          {address.addressLine2 && <div style={{ marginLeft: '22px', color: '#666', fontSize: '13px', marginBottom: '4px' }}>{address.addressLine2}</div>}
          {address.landmark && <div style={{ marginLeft: '22px', color: '#666', fontSize: '12px', marginBottom: '6px', fontStyle: 'italic' }}>Near: {address.landmark}</div>}
          <div style={{ marginLeft: '22px', fontWeight: 600, color: '#333', marginTop: '6px' }}>{address.city}, {address.state} {address.pincode}</div>
          <div style={{ marginLeft: '22px', color: '#666', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>{address.country}</div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: '8px', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
        <button onClick={onEdit} className="btn" style={{ flex: 1, padding: '10px', fontSize: '13px', fontWeight: 600, borderRadius: '8px', backgroundColor: '#fff', color: '#018d71', border: '1.5px solid #018d71', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
          onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#e3f2fd'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
          onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.transform = 'translateY(0)'; }}>
          <Edit2 size={14} />Edit
        </button>
        <button onClick={onDelete} className="btn" style={{ flex: 1, padding: '10px', fontSize: '13px', fontWeight: 600, borderRadius: '8px', backgroundColor: '#fff', color: '#ea4335', border: '1.5px solid #ea4335', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
          onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#fef3f2'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
          onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.transform = 'translateY(0)'; }}>
          <Trash2 size={14} />Remove
        </button>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Main ProfilePage
// ═══════════════════════════════════════════════════════════════════════════════
const ProfilePage = ({ onNavigate, onAddToCart, onOpenCart }) => {
  const VALID_TABS = ['myAccounts', 'myOrders', 'addresses', 'changePassword'];
  const getTabFromHash = () => { const h = window.location.hash.replace('#', ''); return VALID_TABS.includes(h) ? h : 'myAccounts'; };
  const [activeTab, setActiveTabState] = useState(getTabFromHash);
  const setActiveTab = (t) => { setActiveTabState(t); window.location.hash = t; };
  useEffect(() => {
    const fn = () => setActiveTabState(getTabFromHash());
    window.addEventListener('hashchange', fn);
    return () => window.removeEventListener('hashchange', fn);
  }, []);

  const isMounted = useRef(true);
  useEffect(() => { return () => { isMounted.current = false; }; }, []);

  const emptyUser = { id: '', name: '', email: '', phoneNumber: '', dateOfBirth: '', gender: 'Male', password: '' };

  // ── State ──────────────────────────────────────────────────────────────────
  const [userData, setUserData] = useState(emptyUser);
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);

  const [profileLoading, setProfileLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [addressesLoading, setAddressesLoading] = useState(true);

  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [profileErrors, setProfileErrors] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [orderItemsLoading, setOrderItemsLoading] = useState(false);
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [reorderLoading, setReorderLoading] = useState(null);

  const [showAddressForm, setShowAddressForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteInProgress, setDeleteInProgress] = useState(false);

  const [passwordData, setPasswordData] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showPassword, setShowPassword] = useState({ oldPassword: false, newPassword: false, confirmPassword: false });

  // ── Mount: all 3 fire simultaneously ──────────────────────────────────────
  useEffect(() => {
    const userId = localStorage.getItem('userId'), authToken = localStorage.getItem('authToken');
    if (!userId || !authToken) { onNavigate('login'); return; }
    // Fire all 3 at same time — skeletons show immediately while API responds
    fetchUserData();
    fetchUserOrders();
    fetchUserAddresses();
    const guard = setInterval(() => {
      if (!isMounted.current) { clearInterval(guard); return; }
      if (!(localStorage.getItem('isLoggedIn') === 'true' && !!localStorage.getItem('authToken'))) { clearInterval(guard); onNavigate('login'); }
    }, 300);
    return () => clearInterval(guard);
  }, []); // eslint-disable-line

  const fetchUserData = async () => {
    setProfileLoading(true);
    try {
      const userId = localStorage.getItem('userId');
      if (!userId) { onNavigate('login'); return; }
      const res = await apiService.getUserById(userId);
      if (!isMounted.current) return;
      if (res.id) localStorage.setItem('userId', String(res.id));
      setUserData({ id: res.id || userId, name: res.name || '', email: res.email || '', phoneNumber: res.phoneNumber || '', dateOfBirth: res.dateOfBirth || '', gender: res.gender || 'Male', password: res.password || '' });
      setError('');
    } catch (err) {
      if (!isMounted.current) return;
      if (err.response?.status === 404) {
        setError('User not found. Please login again.');
        ['userId', 'authToken'].forEach(k => localStorage.removeItem(k));
        onNavigate('login');
      } else { setError('Failed to load profile. Please try again.'); }
    } finally { if (isMounted.current) setProfileLoading(false); }
  };

  const fetchUserOrders = async () => {
    setOrdersLoading(true);
    try {
      const userId = localStorage.getItem('userId');
      if (!userId) return;
      const res = await apiService.getUserOrders(userId);
      if (isMounted.current) setOrders(res || []);
    } catch (err) {
      console.error('❌ Failed to fetch orders:', err);
      if (isMounted.current) setOrders([]);
    } finally { if (isMounted.current) setOrdersLoading(false); }
  };

  const fetchUserAddresses = async () => {
    setAddressesLoading(true);
    try {
      const userId = localStorage.getItem('userId');
      if (!userId) return;
      const res = await apiService.getUserAddresses(userId);
      if (isMounted.current) setAddresses(res || []);
    } catch (err) {
      console.error('❌ Failed to fetch addresses:', err);
      if (isMounted.current) setAddresses([]);
    } finally { if (isMounted.current) setAddressesLoading(false); }
  };

  const fetchOrderItems = async (orderId) => {
    setOrderItemsLoading(true);
    try {
      const userId = localStorage.getItem('userId');
      if (!userId) return;

      // Fetch order items AND all products simultaneously
      const [res, productsRes] = await Promise.all([
        apiService.getOrderItems(orderId, userId),
        apiService.getAllProductsForDisplay().catch(() => ({ data: [] }))
      ]);

      if (isMounted.current) {
        const items = res || [];
        const allProducts = Array.isArray(productsRes?.data)
          ? productsRes.data
          : Array.isArray(productsRes)
            ? productsRes
            : [];

        console.log('📦 Items:', JSON.stringify(items, null, 2));
        console.log('🛍️ Products sample:', JSON.stringify(allProducts.slice(0, 2), null, 2));

        // Enrich each order item with image from products list
        const enriched = items.map(item => {
          const productId = item.productId ?? item.product?.id ?? item.ProductId ?? 0;

          // Try to find matching product by ID
          const matched =
            allProducts.find(p => p.id === productId) ||
            allProducts.find(p => p.Id === productId) ||
            allProducts[productId - 1]; // fallback: index-based

          const imageUrl = (() => {
            if (matched) {
              // Try imageUrlList first
              const urlList = matched.imageUrlList ?? matched.ImageUrlList ?? matched.imageUrls;
              if (Array.isArray(urlList) && urlList.length > 0) {
                const first = urlList.find(u => u);
                if (first) return toAbsolute(first);
              }
              // Try direct image fields
              const direct =
                matched.imageUrl ?? matched.ImageUrl ??
                matched.image ?? matched.Image ??
                matched.imageURL ?? matched.thumbnail;
              if (direct) return toAbsolute(direct);
            }

            // Fallback: try image fields directly on the item
            const itemImg =
              item.imageUrl ?? item.image ??
              item.product?.imageUrl ?? item.product?.image;
            if (itemImg) return toAbsolute(itemImg);

            return null;
          })();

          return { ...item, _resolvedImage: imageUrl };
        });

        console.log('✅ Enriched items:', enriched.map(i => ({
          name: i.productName,
          image: i._resolvedImage
        })));

        setOrderItems(enriched);
        setSelectedOrder(orderId);
      }
    } catch (err) {
      console.error('❌ Failed to fetch order items:', err);
      if (isMounted.current) setOrderItems([]);
    } finally {
      if (isMounted.current) setOrderItemsLoading(false);
    }
  };

  const toAbsolute = (url) => { if (!url) return null; return url.startsWith('http') ? url : `${BASE_URL}/${url.replace(/^\//, '')}`; };

  const resolveImageFromDisplay = (productId, displayProducts) => {
    if (!displayProducts.length || !productId) return null;
    const dp = displayProducts[productId - 1];
    if (!dp) return null;
    if (Array.isArray(dp.imageUrlList) && dp.imageUrlList.length > 0) { const f = dp.imageUrlList.find(u => u); if (f) return toAbsolute(f); }
    return toAbsolute(dp.imageUrl || dp.image) || null;
  };

  // ── Replace resolveImageFromItem with this ──────────────────────────────────
  const resolveImageFromItem = (item) => {
    // 1. Flat-level imageUrl on the item itself (most reliable from order APIs)
    if (item.imageUrl) return toAbsolute(item.imageUrl);
    if (item.image) return toAbsolute(item.image);

    // 2. Nested under product
    const p = item.product;
    if (p) {
      const urlList = p.imageUrlList ?? p.ImageUrlList ?? p.imageUrls;
      if (Array.isArray(urlList) && urlList.length > 0) {
        const first = urlList.find(u => u);
        if (first) return toAbsolute(first);
      }
      const direct = p.imageUrl ?? p.ImageUrl ?? p.image ?? p.Image ?? p.imageURL;
      if (direct) return toAbsolute(direct);
    }

    // 3. Absolute fallback — use a known-good placeholder
    return `https://placehold.co/60x60?text=No+Image`;
  };

  const handleReorder = async (order) => {
    const orderId = order.id || order.orderId;
    try {
      setReorderLoading(orderId);
      const userId = localStorage.getItem('userId');

      // Use already-fetched enriched items if available, else fetch fresh
      let items =
        selectedOrder === orderId && orderItems.length > 0
          ? orderItems
          : await apiService.getOrderItems(orderId, userId) || [];

      if (items.length === 0) {
        alert('No items found in this order.');
        return;
      }

      // Fetch all products for image + price resolution
      let allProducts = [];
      try {
        const res = await apiService.getAllProductsForDisplay();
        allProducts = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res)
            ? res
            : [];
      } catch (e) {
        console.warn('⚠️ Could not fetch display products:', e.message);
      }

      let successCount = 0;
      const failedNames = [];

      for (const item of items) {
        try {
          const productId =
            item.productId ?? item.product?.id ?? item.ProductId ?? 0;

          const productName =
            item.productName ??
            item.product?.productName ??
            item.product?.name ??
            `Product ${productId}`;

          // ── Resolve image ──────────────────────────────────────────
          // 1. Use _resolvedImage if item was already enriched
          // 2. Match from allProducts by ID
          // 3. Fallback index-based
          // 4. Direct fields on item
          const resolveImage = () => {
            if (item._resolvedImage) return item._resolvedImage;

            const matched =
              allProducts.find(p => p.id === productId) ||
              allProducts.find(p => p.Id === productId) ||
              allProducts[productId - 1];

            if (matched) {
              const urlList =
                matched.imageUrlList ??
                matched.ImageUrlList ??
                matched.imageUrls;
              if (Array.isArray(urlList) && urlList.length > 0) {
                const first = urlList.find(u => u);
                if (first) return toAbsolute(first);
              }
              const direct =
                matched.imageUrl ?? matched.ImageUrl ??
                matched.image ?? matched.Image ??
                matched.imageURL ?? matched.thumbnail;
              if (direct) return toAbsolute(direct);
            }

            // Fallback: item-level fields
            const itemImg =
              item.imageUrl ?? item.image ??
              item.product?.imageUrl ?? item.product?.image;
            if (itemImg) return toAbsolute(itemImg);

            return `${BASE_URL}/uploads/product-images/default-product.png`;
          };

          // ── Resolve sale price ─────────────────────────────────────
          let salePrice = null;
          const matched =
            allProducts.find(p => p.id === productId) ||
            allProducts.find(p => p.Id === productId) ||
            allProducts[productId - 1];

          if (matched) {
            const p = parseFloat(
              String(matched.salePrice ?? matched.SalePrice ?? '').replace(/[^0-9.]/g, '')
            );
            if (!isNaN(p) && p > 0) salePrice = p;
          }

          const image = resolveImage();

          console.log(`🛒 Reorder item: ${productName} | image: ${image}`);

          await onAddToCart({
            id: productId,
            apiProductId: productId,
            name: productName,
            salePrice,
            image,
            description: item.product?.description ?? '',
            category: item.product?.category ?? 'N/A',
          });

          successCount++;
        } catch (err) {
          console.error('❌ Failed to add item:', err);
          failedNames.push(
            item.productName ?? `Product ${item.productId}`
          );
        }
      }

      if (successCount > 0) {
        if (failedNames.length > 0) {
          alert(`${successCount} item(s) added.\nCould not add: ${failedNames.join(', ')}`);
        }
        onOpenCart();
      } else {
        alert('Failed to add items to cart. Please try again.');
      }
    } catch (err) {
      alert(`Failed to reorder: ${err.response?.data?.message || err.message}`);
    } finally {
      if (isMounted.current) setReorderLoading(null);
    }
  };

  const formatDate = (d) => { if (!d) return 'N/A'; try { return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); } catch { return d; } };
  const getOrderStatusBadge = (status) => { const m = { 1: { label: 'Pending', bg: '#fbbc04' }, 2: { label: 'Approved', bg: '#34a853' }, 3: { label: 'Rejected', bg: '#ea4335' } }; const s = m[status] || m[1]; return <span style={{ padding: '4px 12px', backgroundColor: s.bg, color: '#fff', fontSize: '12px', fontWeight: 600, borderRadius: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</span>; };

  const handleAddAddress = () => { setEditingAddress({}); setShowAddressForm(true); };
  const handleEditAddress = (a) => { setEditingAddress({ ...a }); setShowAddressForm(true); };
  const handleDeleteRequest = (a) => setDeleteTarget(a);
  const handleCancelDelete = () => { if (!deleteInProgress) setDeleteTarget(null); };

  const handleConfirmDelete = async () => {
    if (!deleteTarget || deleteInProgress) return;
    const addressId = extractAddressId(deleteTarget);
    if (!addressId) { alert('Cannot delete: address ID not found.'); setDeleteTarget(null); return; }
    setDeleteInProgress(true);
    try { await apiService.deleteAddress(addressId); if (isMounted.current) setAddresses(prev => prev.filter(a => extractAddressId(a) !== addressId)); }
    catch (err) { console.error('❌ deleteAddress error:', err); alert(`Failed to delete address: ${err.response?.data?.message || err.message}`); await fetchUserAddresses(); }
    finally { if (isMounted.current) { setDeleteInProgress(false); setDeleteTarget(null); } }
  };

  const handleSaveAddress = async (formData) => {
    try {
      const addressId = extractAddressId(editingAddress);
      if (addressId) { await apiService.updateAddress(addressId, formData); alert('Address updated successfully!'); }
      else { await apiService.createAddress(formData); alert('Address added successfully!'); }
      await fetchUserAddresses(); setShowAddressForm(false); setEditingAddress(null);
    } catch (err) { alert(`Failed to save address: ${err.response?.data?.message || err.message}`); }
  };

  const handleInputChange = (f, v) => { setUserData(p => ({ ...p, [f]: v })); if (profileErrors[f]) setProfileErrors(p => ({ ...p, [f]: '' })); };
  const validateProfileForm = () => {
    const e = {};
    if (!userData.name.trim()) e.name = 'Name is required';
    else if (userData.name.trim().length < 2) e.name = 'Name must be at least 2 characters';
    if (!userData.email.trim()) e.email = 'Email address is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userData.email.trim())) e.email = 'Please enter a valid email address';
    if (!userData.phoneNumber.trim()) e.phoneNumber = 'Phone number is required';
    else { const r = validatePhoneNumber(userData.phoneNumber.trim()); if (!r.valid) e.phoneNumber = r.message; }
    if (!userData.gender) e.gender = 'Please select a gender';
    setProfileErrors(e); return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validateProfileForm()) return;
    try {
      setSavingProfile(true);
      const userId = userData.id || localStorage.getItem('userId');
      await apiService.updateUserProfile({ id: String(userId), name: userData.name, gender: userData.gender, phoneNumber: userData.phoneNumber, email: userData.email, password: userData.password });
      alert('Profile updated successfully!'); setIsEditing(false); setProfileErrors({}); await fetchUserData();
    } catch (err) {
      const msg = (err.response?.data?.message || err.message || '').toLowerCase();
      if (err.response?.status === 400) {
        if (msg.includes('email')) setProfileErrors(p => ({ ...p, email: 'This email address is already in use.' }));
        else if (msg.includes('phone')) setProfileErrors(p => ({ ...p, phoneNumber: 'This phone number is already in use.' }));
        else setProfileErrors(p => ({ ...p, _form: err.response?.data?.message || 'Please check your details and try again.' }));
      } else setProfileErrors(p => ({ ...p, _form: `Failed to update profile: ${err.response?.data?.message || err.message}` }));
    } finally { if (isMounted.current) setSavingProfile(false); }
  };

  const handlePasswordChange = (f, v) => { setPasswordData(p => ({ ...p, [f]: v })); if (passwordErrors[f]) setPasswordErrors(p => ({ ...p, [f]: '' })); };
  const togglePasswordVisibility = (f) => setShowPassword(p => ({ ...p, [f]: !p[f] }));
  const validatePassword = () => {
    const e = {};
    if (!passwordData.oldPassword) e.oldPassword = 'Current password is required';
    if (!passwordData.newPassword) e.newPassword = 'New password is required';
    else if (passwordData.newPassword.length < 8) e.newPassword = 'Password must be at least 8 characters';
    if (!passwordData.confirmPassword) e.confirmPassword = 'Please confirm your password';
    else if (passwordData.newPassword !== passwordData.confirmPassword) e.confirmPassword = 'Passwords do not match';
    if (passwordData.oldPassword && passwordData.newPassword && passwordData.oldPassword === passwordData.newPassword) e.newPassword = 'New password must be different from current password';
    return e;
  };

  const menuItems = [
    { id: 'myAccounts', icon: User, label: 'My Account' },
    { id: 'myOrders', icon: ShoppingBag, label: 'My Orders' },
    { id: 'addresses', icon: MapPin, label: 'Addresses' },
    { id: 'changePassword', icon: Lock, label: 'Security' },
  ];

  const renderUserAvatar = (size = 80) => {
    const src = userData.gender === 'Male' ? maleAvatar : femaleAvatar;
    return <img src={src} alt="Avatar" style={{ width: `${size}px`, height: `${size}px`, borderRadius: '50%', objectFit: 'cover', border: '3px solid #fff', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }} onError={e => e.target.style.display = 'none'} />;
  };

  const inputStyle = { padding: '10px 14px', borderRadius: '6px', border: '1.5px solid #ddd', fontSize: '14px', color: '#333', backgroundColor: '#fff', transition: 'border-color 0.2s' };
  const readonlyInputStyle = { ...inputStyle, backgroundColor: '#f8f9fa', color: '#666', cursor: 'not-allowed' };
  const fieldErrStyle = { fontSize: '12px', color: '#ea4335', marginTop: '5px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '4px' };
  const sectionTitle = (title, subtitle) => (<div style={{ marginBottom: '24px' }}><h4 style={{ fontSize: '24px', fontWeight: 700, color: '#333', marginBottom: '4px' }}>{title}</h4>{subtitle && <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>{subtitle}</p>}</div>);
  const fieldLabel = (label, required = false) => (<label style={{ fontSize: '13px', fontWeight: 600, color: '#444', marginBottom: '6px', display: 'block' }}>{label}{required && <span style={{ color: '#ea4335', marginLeft: '3px' }}>*</span>}</label>);

  const renderContent = () => {
    switch (activeTab) {

      case 'myAccounts':
        // Show skeleton while loading — feels instant
        if (profileLoading) return <ProfileSkeleton />;
        if (error) return (
          <div className="text-center" style={{ padding: '60px 20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
            <h5 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '10px', color: '#ea4335' }}>{error}</h5>
            <button onClick={fetchUserData} className="btn btn-primary mt-3" style={{ fontSize: '14px', padding: '10px 20px' }}>Retry</button>
          </div>
        );
        return (
          <div>
            {sectionTitle('Personal Information', 'Manage your account details')}
            <div className="text-center mb-4">
              {renderUserAvatar(100)}
              <h5 style={{ fontSize: '20px', fontWeight: 700, marginTop: '16px', marginBottom: '4px' }}>{userData.name || 'User'}</h5>
              <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>{userData.email}</p>
            </div>
            {profileErrors._form && (<div style={{ marginBottom: '16px', padding: '12px 16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}><span style={{ fontSize: '18px' }}>⚠️</span><span style={{ fontSize: '14px', color: '#dc2626', fontWeight: 500 }}>{profileErrors._form}</span></div>)}
            {!isEditing ? (
              <button onClick={() => setIsEditing(true)} className="btn btn-primary d-flex align-items-center gap-2" style={{ padding: '10px 20px', fontSize: '14px', fontWeight: 600, borderRadius: '6px', backgroundColor: '#018d71', borderColor: '#018d71' }}><Edit2 size={16} />Edit Profile</button>
            ) : (
              <div className="d-flex gap-2 mb-4">
                <button onClick={() => { setIsEditing(false); setProfileErrors({}); fetchUserData(); }} className="btn btn-outline-secondary">Cancel</button>
                <button onClick={handleSave} disabled={savingProfile} className="btn btn-primary" style={{ backgroundColor: '#018d71', borderColor: '#018d71' }}>{savingProfile ? 'Saving...' : 'Save Changes'}</button>
              </div>
            )}
            <div className="row g-3 mt-3">
              <div className="col-12">
                {fieldLabel('Name', true)}
                <input type="text" className="form-control" value={userData.name} onChange={e => handleInputChange('name', e.target.value)} readOnly={!isEditing}
                  style={isEditing ? { ...inputStyle, border: `1.5px solid ${profileErrors.name ? '#ea4335' : '#ddd'}` } : readonlyInputStyle}
                  onFocus={e => { if (isEditing) e.target.style.borderColor = profileErrors.name ? '#ea4335' : '#018d71'; }}
                  onBlur={e => { if (isEditing) e.target.style.borderColor = profileErrors.name ? '#ea4335' : '#ddd'; }} />
                {profileErrors.name && <div style={fieldErrStyle}><span>⚠️</span> {profileErrors.name}</div>}
              </div>
              <div className="col-12">
                {fieldLabel('Gender', true)}
                <div className="d-flex gap-3" style={{ marginTop: '4px' }}>
                  {['Male', 'Female'].map(g => (
                    <label key={g} className="d-flex align-items-center gap-2" style={{ cursor: isEditing ? 'pointer' : 'not-allowed', padding: '6px 0' }}>
                      <input type="radio" name="gender" value={g} checked={userData.gender === g} onChange={e => handleInputChange('gender', e.target.value)} disabled={!isEditing} />
                      <span style={{ fontSize: '14px', color: '#333' }}>{g}</span>
                    </label>
                  ))}
                </div>
                {profileErrors.gender && <div style={fieldErrStyle}><span>⚠️</span> {profileErrors.gender}</div>}
              </div>
              <div className="col-12">
                {fieldLabel('Phone Number', true)}
                {isEditing ? (
                  <><PhoneInput value={userData.phoneNumber} onChange={e => handleInputChange('phoneNumber', e.target.value)}
                    onBlur={() => { if (userData.phoneNumber) { const r = validatePhoneNumber(userData.phoneNumber); if (!r.valid) setProfileErrors(p => ({ ...p, phoneNumber: r.message })); else setProfileErrors(p => ({ ...p, phoneNumber: '' })); } }}
                    error={profileErrors.phoneNumber} disabled={false} />
                    {profileErrors.phoneNumber && <div style={fieldErrStyle}><span>⚠️</span> {profileErrors.phoneNumber}</div>}</>
                ) : (
                  <input type="tel" className="form-control" value={userData.phoneNumber} readOnly style={readonlyInputStyle} />
                )}
              </div>
              <div className="col-12">
                {fieldLabel('Email', true)}
                <input type="email" className="form-control" value={userData.email} onChange={e => handleInputChange('email', e.target.value)} readOnly={!isEditing}
                  style={isEditing ? { ...inputStyle, border: `1.5px solid ${profileErrors.email ? '#ea4335' : '#ddd'}` } : readonlyInputStyle}
                  onFocus={e => { if (isEditing) e.target.style.borderColor = profileErrors.email ? '#ea4335' : '#018d71'; }}
                  onBlur={e => { if (isEditing) e.target.style.borderColor = profileErrors.email ? '#ea4335' : '#ddd'; }} />
                {profileErrors.email && <div style={fieldErrStyle}><span>⚠️</span> {profileErrors.email}</div>}
              </div>
            </div>
          </div>
        );

      case 'myOrders':
        if (ordersLoading) return <OrdersSkeleton />;
        const filteredOrders = orderStatusFilter === 'all' ? orders : orders.filter(o => o.orderStatus === parseInt(orderStatusFilter));
        return (
          <div>
            {sectionTitle('My Orders', 'Track and manage your orders')}
            <div className="d-flex align-items-center gap-2 mb-4">
              <label style={{ fontSize: '14px', fontWeight: 600, color: '#444' }}>Filter:</label>
              <select value={orderStatusFilter} onChange={e => setOrderStatusFilter(e.target.value)} className="form-select"
                style={{ padding: '8px 32px 8px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px', fontWeight: 500, color: '#333', backgroundColor: '#fff', cursor: 'pointer', minWidth: '150px' }}>
                <option value="all">All Orders ({orders.length})</option>
                <option value="1">Pending ({orders.filter(o => o.orderStatus === 1).length})</option>
                <option value="2">Approved ({orders.filter(o => o.orderStatus === 2).length})</option>
                <option value="3">Rejected ({orders.filter(o => o.orderStatus === 3).length})</option>
              </select>
            </div>
            {filteredOrders.length === 0 ? (
              <div className="text-center" style={{ padding: '60px 20px', backgroundColor: '#f8f9fa', borderRadius: '10px' }}>
                <ShoppingBag size={64} style={{ color: '#bbb', marginBottom: '20px' }} />
                <h5 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '10px', color: '#666' }}>{orderStatusFilter === 'all' ? 'No orders yet' : 'No orders found'}</h5>
                <p style={{ fontSize: '14px', color: '#888', marginBottom: '0' }}>{orderStatusFilter === 'all' ? 'Start shopping to see your orders here' : 'No orders match the selected status'}</p>
                {orderStatusFilter !== 'all' && <button onClick={() => setOrderStatusFilter('all')} className="btn btn-outline-primary mt-3" style={{ fontSize: '14px', padding: '8px 20px' }}>Show All Orders</button>}
              </div>
            ) : (
              <div>
                {orderStatusFilter !== 'all' && (
                  <div style={{ marginBottom: '16px', padding: '12px 16px', backgroundColor: '#e3f2fd', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '13px', color: '#018d71', fontWeight: 600 }}>Showing {filteredOrders.length} of {orders.length} orders</span>
                    <button onClick={() => setOrderStatusFilter('all')} style={{ background: 'none', border: 'none', color: '#018d71', fontSize: '13px', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}>Clear Filter</button>
                  </div>
                )}
                {filteredOrders.map((order) => {
                  const orderId = order.id || order.orderId, isReordering = reorderLoading === orderId, isExpanded = selectedOrder === orderId;
                  return (
                    <div key={orderId} style={{ marginBottom: '20px', backgroundColor: '#fff', borderRadius: '10px', border: '1px solid #e0e0e0', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                      <div style={{ padding: '20px', backgroundColor: '#f8f9fa', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e0e0e0' }}>
                        <div><div style={{ fontSize: '16px', fontWeight: 700, color: '#333', marginBottom: '4px' }}>Order #{orderId}</div><div style={{ fontSize: '13px', color: '#666' }}>{formatDate(order.orderDate || order.createdDate)}</div></div>
                        {getOrderStatusBadge(order.orderStatus)}
                      </div>
                      <div style={{ padding: '20px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px', marginBottom: '16px' }}>
                          {[['Subtotal', order.subTotal], ['Tax', order.taxAmount], ['Shipping', order.shippingAmount]].map(([l, v]) => (
                            <div key={l}><div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>{l}</div><div style={{ fontSize: '16px', fontWeight: 600, color: '#333' }}>${v?.toFixed(2) || '0.00'}</div></div>
                          ))}
                          <div><div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Total</div><div style={{ fontSize: '18px', fontWeight: 700, color: '#018d71' }}>${((order.subTotal || 0) + (order.taxAmount || 0) + (order.shippingAmount || 0) - (order.discountAmount || 0)).toFixed(2)}</div></div>
                        </div>
                        <div style={{ display: 'flex', gap: '12px' }}>
                          <button onClick={() => { if (isExpanded) { setSelectedOrder(null); setOrderItems([]); } else { fetchOrderItems(orderId); } }}
                            className="btn btn-outline flex-fill d-flex align-items-center justify-content-center gap-2"
                            style={{ fontSize: '14px', fontWeight: 600, padding: '10px', borderColor: '#018d71', color: '#018d71' }}>
                            <Eye size={16} />{isExpanded ? 'Hide Items' : 'View Items'}
                          </button>
                          <button onClick={() => handleReorder(order)} disabled={isReordering}
                            className="btn flex-fill d-flex align-items-center justify-content-center gap-2"
                            style={{ fontSize: '14px', fontWeight: 600, padding: '10px', borderRadius: '6px', border: 'none', background: isReordering ? '#e8f5e9' : 'linear-gradient(135deg, #34a853 0%, #2d9249 100%)', color: isReordering ? '#2d9249' : '#fff', cursor: isReordering ? 'not-allowed' : 'pointer' }}>
                            {isReordering ? (<><RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />Adding to Cart...</>) : (<><ShoppingCart size={16} />Reorder</>)}
                          </button>
                        </div>
                        {isExpanded && (
                          <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e0e0e0' }}>
                            {orderItemsLoading
                              ? <div className="text-center py-4"><div className="spinner-border spinner-border-sm text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
                              : orderItems.length === 0
                                ? <div className="text-center text-muted py-3">No items found</div>
                                : orderItems.map((item, idx) => (
                                  <div key={idx} style={{ display: 'flex', gap: '16px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px', marginBottom: '12px' }}>
                                    <img
                                      src={item._resolvedImage || `https://placehold.co/60x60/f0f0f0/999?text=?`}
                                      alt={item.productName || 'Product'}
                                      style={{
                                        width: '60px',
                                        height: '60px',
                                        borderRadius: '6px',
                                        objectFit: 'cover',
                                        flexShrink: 0,
                                        backgroundColor: '#f0f0f0'
                                      }}
                                      onError={e => {
                                        e.target.onerror = null;
                                        e.target.src = `https://placehold.co/60x60/f0f0f0/999?text=?`;
                                      }}
                                    />
                                    <div style={{ flex: 1 }}>
                                      <div style={{ fontSize: '14px', fontWeight: 600, color: '#333', marginBottom: '4px' }}>{item.productName || 'Product'}</div>
                                      <div style={{ fontSize: '13px', color: '#666' }}>Qty: {item.quantity} × ${item.unitPrice?.toFixed(2)}</div>
                                    </div>
                                    <div style={{ fontSize: '16px', fontWeight: 700, color: '#018d71' }}>${((item.quantity || 0) * (item.unitPrice || 0)).toFixed(2)}</div>
                                  </div>
                                ))
                            }
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );

      case 'addresses':
        if (addressesLoading) return <AddressesSkeleton />;
        return (
          <div>
            {sectionTitle('My Addresses', 'Manage your shipping and billing addresses')}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', padding: '16px 20px', backgroundColor: '#f8f9fa', borderRadius: '10px', border: '1px solid #e9ecef' }}>
              <div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#333', marginBottom: '4px' }}>📍 Saved Addresses</div>
                <div style={{ fontSize: '13px', color: '#6c757d' }}>{addresses.length} {addresses.length === 1 ? 'address' : 'addresses'} on file</div>
              </div>
              {!showAddressForm && (<button onClick={handleAddAddress} className="btn btn-primary d-flex align-items-center gap-2" style={{ fontSize: '14px', fontWeight: 600, padding: '10px 24px', borderRadius: '8px', background: 'linear-gradient(135deg, #018d71 0%, #018d71 100%)', border: 'none' }}><Plus size={18} />Add New Address</button>)}
            </div>
            {showAddressForm && (
              <div style={{ marginBottom: '32px', padding: '28px', backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #dee2e6', boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', paddingBottom: '16px', borderBottom: '2px solid #f0f0f0' }}>
                  <h5 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#1a1a1a', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {extractAddressId(editingAddress) ? <><Edit2 size={22} style={{ color: '#018d71' }} />Edit Address</> : <><Plus size={22} style={{ color: '#34a853' }} />Add New Address</>}
                  </h5>
                  <button onClick={() => { setShowAddressForm(false); setEditingAddress(null); }} style={{ background: 'none', border: 'none', fontSize: '24px', color: '#999', cursor: 'pointer', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%' }}>×</button>
                </div>
                <AddressForm initial={editingAddress} onSave={handleSaveAddress} onCancel={() => { setShowAddressForm(false); setEditingAddress(null); }} />
              </div>
            )}
            {addresses.length === 0 && !showAddressForm ? (
              <div className="text-center" style={{ padding: '80px 20px', backgroundColor: '#fafbfc', borderRadius: '12px', border: '2px dashed #d1d5db' }}>
                <div style={{ width: '120px', height: '120px', margin: '0 auto 24px', background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><MapPin size={56} style={{ color: '#018d71' }} /></div>
                <h5 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '12px', color: '#333' }}>No addresses saved yet</h5>
                <p style={{ fontSize: '14px', color: '#6c757d', marginBottom: '24px', maxWidth: '400px', margin: '0 auto 24px' }}>Add your first delivery address to make checkout faster and easier</p>
                <button onClick={handleAddAddress} className="btn btn-primary d-flex align-items-center gap-2 mx-auto" style={{ fontSize: '14px', fontWeight: 600, padding: '12px 28px', borderRadius: '8px' }}><Plus size={18} />Add Your First Address</button>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '20px', marginBottom: '20px' }}>
                {addresses.map((addr, idx) => (
                  <AddressCard key={extractAddressId(addr) || idx} address={addr} onEdit={() => handleEditAddress(addr)} onDelete={() => handleDeleteRequest(addr)} />
                ))}
              </div>
            )}
          </div>
        );

      case 'changePassword':
        return (
          <div>
            {sectionTitle('Change Password', 'Update your password to keep your account secure')}
            <div style={{ maxWidth: '500px' }}>
              {passwordErrors.submit && <div className="alert alert-danger" role="alert" style={{ fontSize: '14px' }}>{passwordErrors.submit}</div>}
              {[{ field: 'oldPassword', label: 'Current Password' }, { field: 'newPassword', label: 'New Password' }, { field: 'confirmPassword', label: 'Confirm New Password' }].map(({ field, label }) => (
                <div key={field} className="mb-3">
                  <label style={{ fontSize: '13px', fontWeight: 600, color: '#444', marginBottom: '6px', display: 'block' }}>{label}</label>
                  <div style={{ position: 'relative' }}>
                    <input type={showPassword[field] ? 'text' : 'password'} className="form-control" value={passwordData[field]} onChange={e => handlePasswordChange(field, e.target.value)}
                      style={{ padding: '10px 40px 10px 14px', borderRadius: '6px', border: `1px solid ${passwordErrors[field] ? '#ea4335' : '#ddd'}`, fontSize: '14px' }} />
                    <button type="button" onClick={() => togglePasswordVisibility(field)}
                      style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '5px', display: 'flex', alignItems: 'center', color: '#666' }}
                      onMouseEnter={e => e.currentTarget.style.color = '#018d71'} onMouseLeave={e => e.currentTarget.style.color = '#666'}>
                      {showPassword[field] ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {passwordErrors[field] && <div style={{ fontSize: '12px', color: '#ea4335', marginTop: '4px', fontWeight: 500 }}>{passwordErrors[field]}</div>}
                </div>
              ))}
              <button
                onClick={async () => {
                  const e = validatePassword();
                  if (Object.keys(e).length > 0) {
                    setPasswordErrors(e);
                    return;
                  }
                  try {
                    setPasswordLoading(true);
                    setPasswordErrors({});
                    await apiService.changePassword({
                      oldPassword: passwordData.oldPassword,
                      newPassword: passwordData.newPassword
                    });
                    alert('Password changed successfully!');
                    setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
                  } catch (err) {
                    let msg = 'Failed to change password. Please try again.';
                    if (err.response?.status === 400) msg = 'Current password is incorrect.';
                    else if (err.response?.data?.message) msg = err.response.data.message;
                    setPasswordErrors({ submit: msg });
                  } finally {
                    if (isMounted.current) setPasswordLoading(false);
                  }
                }}
                disabled={passwordLoading}
                className="text-start d-flex align-items-center gap-3"
                style={{
                  padding: '12px 16px',
                  borderRadius: '6px',
                  backgroundColor: 'rgb(1, 141, 113)',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 600,
                  border: 'none',
                  transition: '0.3s'
                }}
              >
                {passwordLoading ? 'Changing Password...' : 'Change Password'}
              </button>
            </div>
          </div>
        );

      default: return null;
    }
  };

  return (
    <div className="container-fluid" style={{ backgroundColor: '#f5f5f5', minHeight: '100vh', padding: '24px 0' }}>
      {deleteTarget && (<DeleteConfirmModal address={deleteTarget} onConfirm={handleConfirmDelete} onCancel={handleCancelDelete} deleting={deleteInProgress} />)}
      <div className="row g-0">
        <div className="col-lg-3 d-none d-lg-block">
          <div style={{ position: 'sticky', top: '24px', padding: '0 16px' }}>
            <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <div className="text-center mb-4">
                {profileLoading
                  ? <><div style={{ width: 80, height: 80, borderRadius: '50%', background: 'linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite', margin: '0 auto 12px' }} /><Sk w={100} h={14} mb={4} r={4} /><Sk w={130} h={18} r={4} /></>
                  : <>{renderUserAvatar(80)}<div style={{ fontSize: '11px', fontWeight: 600, color: '#999', marginTop: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>WELCOME</div><div style={{ fontSize: '18px', fontWeight: 700, color: '#333', marginTop: '4px' }}>{userData.name || 'User'}</div></>
                }
              </div>
              <div className="d-flex flex-column gap-2">
                {menuItems.map(item => {
                  const Icon = item.icon; const active = activeTab === item.id;
                  return (
                    <button key={item.id} onClick={() => { setActiveTab(item.id); setShowAddressForm(false); setProfileErrors({}); }}
                      className="btn text-start d-flex align-items-center gap-3"
                      style={{ padding: '12px 16px', borderRadius: '6px', backgroundColor: active ? '#018d71' : 'transparent', color: active ? '#fff' : '#444', fontSize: '14px', fontWeight: active ? 600 : 500, border: 'none', transition: 'all 0.2s' }}
                      onMouseEnter={e => { if (!active) e.currentTarget.style.backgroundColor = '#f8f9fa'; }}
                      onMouseLeave={e => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}>
                      <Icon size={20} />{item.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 d-lg-none mb-3">
          <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <div className="d-flex align-items-center gap-3 mb-3">
              {profileLoading
                ? <><div style={{ width: 50, height: 50, borderRadius: '50%', background: 'linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite', flexShrink: 0 }} /><div><Sk w={60} h={10} mb={6} r={3} /><Sk w={100} h={16} r={3} /></div></>
                : <>{renderUserAvatar(50)}<div><div style={{ fontSize: '10px', fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: '1px' }}>WELCOME</div><div style={{ fontSize: '16px', fontWeight: 700, color: '#333' }}>{userData.name || 'User'}</div></div></>
              }
            </div>
            <div className="row g-2">
              {menuItems.map(item => {
                const Icon = item.icon; const active = activeTab === item.id;
                return (
                  <div key={item.id} className="col-6">
                    <button onClick={() => { setActiveTab(item.id); setShowAddressForm(false); setProfileErrors({}); }}
                      className="btn w-100 d-flex flex-column align-items-center gap-2 p-3"
                      style={{ borderRadius: '6px', backgroundColor: active ? '#018d71' : '#f8f9fa', color: active ? '#fff' : '#444', fontSize: '12px', fontWeight: active ? 600 : 500, border: 'none' }}>
                      <Icon size={20} />{item.label}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="col-lg-9">
          <div style={{ padding: '0 16px' }}>
            <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '32px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', minHeight: '600px' }}>
              {renderContent()}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
        .form-control:focus { box-shadow: none; }
      `}</style>
    </div>
  );
};

export default ProfilePage;